/**
 * What a page with a mouse adds to a phone's UI: `hover` events (`.onHover`),
 * `continuoushover` events (`.onContinuousHover`: where the mouse is as it
 * moves), tooltips (`.help`), cursors (`.pointerStyle`), and SF Symbols'
 * effects (`.symbolEffect`) drawn with CSS. The Swift shim draws an iPhone
 * and sends none of these; producers in the page (@swiftbrowser/react) do.
 */

import type { ContinuousHoverEvent, ElementId, HoverEvent, Style, SymbolEffectStyle } from './protocol';

// ---------------------------------------------------------------------------
// Hover
// ---------------------------------------------------------------------------

const HOVER_ATTR = 'sbHover';
const CONTINUOUS_HOVER_ATTR = 'sbContinuousHover';

/**
 * Sends `hover` as a mouse enters and leaves the boxes marked `data-sb-hover`
 * under `root`, and `continuoushover` as it moves over the boxes marked
 * `data-sb-continuous-hover` (its value the space of the location: the box's
 * own, or the root's), one per animation frame per box, then `ended` as it
 * leaves each.
 */
export class HoverTracker {
  private readonly hovered = new Set<HTMLElement>();
  /** The boxes the mouse is over that follow it, with the location to send at the next frame. */
  private readonly followed = new Map<HTMLElement, { x: number; y: number } | null>();
  private frame: number | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly send: (event: HoverEvent | ContinuousHoverEvent) => void,
  ) {
    root.addEventListener('pointerover', this.onOver);
    root.addEventListener('pointerout', this.onOut);
    root.addEventListener('pointermove', this.onMove);
  }

  dispose(): void {
    this.root.removeEventListener('pointerover', this.onOver);
    this.root.removeEventListener('pointerout', this.onOut);
    this.root.removeEventListener('pointermove', this.onMove);
    this.hovered.clear();
    this.followed.clear();
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  private readonly onOver = (event: PointerEvent): void => {
    if (event.pointerType === 'touch') return;
    for (let el = event.target as HTMLElement | null; el && el !== this.root; el = el.parentElement) {
      if (el.dataset[HOVER_ATTR] !== undefined && !this.hovered.has(el)) {
        this.hovered.add(el);
        this.report(el, true);
      }
      if (el.dataset[CONTINUOUS_HOVER_ATTR] !== undefined && !this.followed.has(el)) this.followed.set(el, this.locationIn(el, event));
    }
    if (this.followed.size) this.schedule();
  };

  private readonly onMove = (event: PointerEvent): void => {
    if (event.pointerType === 'touch' || !this.followed.size) return;
    for (let el = event.target as HTMLElement | null; el && el !== this.root; el = el.parentElement) {
      if (this.followed.has(el)) this.followed.set(el, this.locationIn(el, event));
    }
    this.schedule();
  };

  private readonly onOut = (event: PointerEvent): void => {
    if (event.pointerType === 'touch') return;
    const next = event.relatedTarget as Node | null;
    for (const el of [...this.hovered]) {
      if (next && el.contains(next)) continue;
      this.hovered.delete(el);
      this.report(el, false);
    }
    for (const el of [...this.followed.keys()]) {
      if (next && el.contains(next)) continue;
      this.followed.delete(el);
      const id = this.idOf(el);
      if (id !== null) this.send({ type: 'continuoushover', id, phase: 'ended' });
    }
  };

  /** Where the mouse is, in the box's own coordinates or the root's, as the box's style asks. */
  private locationIn(el: HTMLElement, event: PointerEvent): { x: number; y: number } {
    const rect = (el.dataset[CONTINUOUS_HOVER_ATTR] === 'global' ? this.root : el).getBoundingClientRect();
    return { x: Math.round((event.clientX - rect.left) * 100) / 100, y: Math.round((event.clientY - rect.top) * 100) / 100 };
  }

  /** The followed boxes' locations go out at the next frame, however many moves came in. */
  private schedule(): void {
    if (this.frame !== null) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      for (const [el, location] of this.followed) {
        if (!location) continue;
        this.followed.set(el, null);
        const id = this.idOf(el);
        if (id !== null) this.send({ type: 'continuoushover', id, phase: 'active', location });
      }
    });
  }

  private idOf(el: HTMLElement): ElementId | null {
    const id = Number(el.dataset['sbId']);
    return Number.isInteger(id) && el.isConnected ? id : null;
  }

  private report(el: HTMLElement, value: boolean): void {
    const id = this.idOf(el);
    if (id !== null) this.send({ type: 'hover', id, value });
  }
}

// ---------------------------------------------------------------------------
// Style fields
// ---------------------------------------------------------------------------

/** One run of each effect at speed 1 (seconds) and its keyframes on the CSS `scale`, `rotate`, `translate` and `opacity` properties. */
const EFFECTS: Record<string, { duration: number; frames: Keyframe[] }> = {
  bounce: {
    duration: 0.5,
    frames: [
      { scale: '1', translate: '0 0', offset: 0 },
      { scale: '1.18', translate: '0 -12%', offset: 0.35 },
      { scale: '0.94', translate: '0 0', offset: 0.7 },
      { scale: '1', translate: '0 0', offset: 1 },
    ],
  },
  pulse: { duration: 1.2, frames: [{ opacity: '1' }, { opacity: '0.35' }, { opacity: '1' }] },
  variableColor: { duration: 1.2, frames: [{ opacity: '1' }, { opacity: '0.5', offset: 0.33 }, { opacity: '0.75', offset: 0.66 }, { opacity: '1' }] },
  rotate: { duration: 1, frames: [{ rotate: '0deg' }, { rotate: '360deg' }] },
  wiggle: {
    duration: 0.6,
    frames: [{ rotate: '0deg' }, { rotate: '-14deg', offset: 0.25 }, { rotate: '14deg', offset: 0.6 }, { rotate: '0deg' }],
  },
  breathe: { duration: 2, frames: [{ scale: '1', opacity: '1' }, { scale: '1.1', opacity: '0.7' }, { scale: '1', opacity: '1' }] },
};

const playing = new WeakMap<HTMLElement, { key: string; animation: globalThis.Animation }>();
const triggers = new WeakMap<HTMLElement, number>();

/** Apply the pointer and effect fields of a `styled` element's style (called on create and update). */
export function applyPointerAndEffects(el: HTMLElement, style: Style): void {
  if (style.hover) el.dataset[HOVER_ATTR] = '';
  if (style.continuousHover) el.dataset[CONTINUOUS_HOVER_ATTR] = style.continuousHover;
  else delete el.dataset[CONTINUOUS_HOVER_ATTR];
  if (style.help) {
    el.title = style.help;
    el.dataset['sbHelp'] = '';
  } else if (el.hasAttribute('title') && el.classList.contains('sb-styled')) {
    el.removeAttribute('title');
  }
  if (style.pointerStyle) el.style.cursor = style.pointerStyle;
  applySymbolEffect(el, style.symbolEffect);
  if (style.matchedGeometry) el.dataset['sbMatched'] = style.matchedGeometry;
  if (style.contentTransition) {
    el.dataset['sbContentTransition'] = style.contentTransition.kind;
    const { countsDown } = style.contentTransition;
    if (countsDown === undefined) delete el.dataset['sbCountsDown'];
    else el.dataset['sbCountsDown'] = String(countsDown);
  }
}

function applySymbolEffect(el: HTMLElement, effect: SymbolEffectStyle | undefined): void {
  const s = el.style;
  if (!effect) {
    stopEffect(el);
    return;
  }
  el.dataset['sbSymbolEffect'] = effect.effect;
  // State effects: a look kept while the style carries them.
  if (effect.effect === 'scale') {
    s.transition = 'scale 0.25s';
    s.scale = effect.direction === 'down' ? '0.85' : '1.2';
    return;
  }
  if (effect.effect === 'appear' || effect.effect === 'disappear') {
    s.transition = 'opacity 0.25s, scale 0.25s';
    const shown = effect.effect === 'appear';
    s.opacity = shown ? '1' : '0';
    s.scale = shown ? '1' : '0.6';
    return;
  }
  const spec = EFFECTS[effect.effect];
  if (!spec || typeof el.animate !== 'function') return;
  const duration = (spec.duration / Math.max(effect.speed ?? 1, 0.01)) * 1000;
  const repeat = effect.repeat === 'forever' ? Infinity : Math.max(1, effect.repeat ?? 1);
  if (effect.trigger !== undefined) {
    // A discrete effect plays once per new trigger (never for the first value).
    const last = triggers.get(el);
    triggers.set(el, effect.trigger);
    if (last === undefined || last === effect.trigger) return;
    playing.get(el)?.animation.cancel();
    playing.set(el, { key: `once:${effect.trigger}`, animation: el.animate(spec.frames, { duration, iterations: repeat, easing: 'ease-in-out' }) });
    return;
  }
  // An indefinite effect keeps running across updates that leave it alone.
  const key = `${effect.effect}:${duration}:${repeat === Infinity ? 'forever' : repeat}`;
  const current = playing.get(el);
  if (current && current.key === key && current.animation.playState !== 'finished') return;
  current?.animation.cancel();
  const iterations = effect.repeat === undefined ? Infinity : repeat;
  playing.set(el, { key, animation: el.animate(spec.frames, { duration, iterations, easing: 'ease-in-out' }) });
}

function stopEffect(el: HTMLElement): void {
  const current = playing.get(el);
  if (current && !current.key.startsWith('once:')) {
    current.animation.cancel();
    playing.delete(el);
  }
  triggers.delete(el);
}

// ---------------------------------------------------------------------------
// Geometry readers that report their frame
// ---------------------------------------------------------------------------

export interface SafeAreaInsets {
  top: number;
  leading: number;
  bottom: number;
  trailing: number;
}

/** How far the device's safe area reaches into a box at `rect` in a root of `size`. */
export function safeAreaOverlap(
  rect: { x: number; y: number; width: number; height: number },
  size: { width: number; height: number },
  safeArea: SafeAreaInsets,
): SafeAreaInsets {
  const clamp = (value: number, max: number): number => Math.round(Math.min(Math.max(value, 0), max));
  return {
    top: clamp(safeArea.top - rect.y, rect.height),
    bottom: clamp(rect.y + rect.height - (size.height - safeArea.bottom), rect.height),
    leading: clamp(safeArea.leading - rect.x, rect.width),
    trailing: clamp(rect.x + rect.width - (size.width - safeArea.trailing), rect.width),
  };
}
